/**
 * RunDetail — the expanded view of one BHA run: the bottom-up component list,
 * the bit facts, the IADC 8-point dull grade, and the bit-ops grid (ROP / WOB /
 * RPM / flow at depth). Everything is served by Oxbo-P; lengths/depths are
 * converted to the viewer's profile, ROP is display-only.
 */
import type { BhaRunWithChildren, BhaComponentOut } from "@api/types";
import { useLocale } from "@hooks/useLocale";
import { fmtNum, fmtHours } from "@lib/format";
import type { UseUnits } from "@units/useUnits";
import { inchFrac } from "./inchFrac";

/** IADC dull grade is 8 dash-joined codes: I O D L B G O R. */
const DULL_KEYS = ["I", "O", "D", "L", "B", "G", "O", "R"] as const;

interface Props {
  run: BhaRunWithChildren;
  units: UseUnits;
  storedProfile: string | null | undefined;
  vendorLabel: (code: string | null) => string;
  typeLabel: (code: string) => string;
}

export function RunDetail({ run, units, storedProfile, vendorLabel, typeLabel }: Props) {
  const { t, locale } = useLocale();
  const bottomUp = [...run.assembly].sort((a, b) => a.position - b.position);
  const dull = run.dull_grade ? run.dull_grade.split("-") : null;

  const depth = (v: number | null) => units.fmt(units.convertField("assembly", "md_top", v, storedProfile));
  const len = (v: number | null) =>
    v == null ? "—" : units.fmt(units.convertField("assembly", "md_top", v, storedProfile));

  return (
    <div className="card" style={{ padding: 16 }}>
      {/* components, bottom-up */}
      <h2 className="bha-subhead">{t("bha.components")}</h2>
      <div className="comp-list">
        <div className="comp-row head">
          <div>{t("bha.comp.type")}</div>
          <div>{t("bha.comp.od")} (in)</div>
          <div>{t("bha.comp.length")}</div>
          <div>{t("bha.comp.vendor")}</div>
        </div>
        {bottomUp.map((c) => (
          <ComponentRow
            key={c.id}
            comp={c}
            typeLabel={typeLabel}
            vendorLabel={vendorLabel}
            len={len}
          />
        ))}
      </div>

      {/* bit facts */}
      <h2 className="bha-subhead">{t("bha.bit")}</h2>
      <dl className="bit-facts">
        <dt>{t("bha.bit.size")}</dt>
        <dd className="mono">
          {inchFrac(run.bit_size_entry_key)}
          {run.bit_iadc ? ` · ${run.bit_iadc}` : ""}
        </dd>
        {run.bit_jets ? (
          <>
            <dt>{t("bha.bit.jets")}</dt>
            <dd className="mono">{run.bit_jets} (32nds)</dd>
          </>
        ) : null}
        <dt>{t("bha.bit.inOut")}</dt>
        <dd className="mono">
          {depth(run.md_in)} → {run.md_out == null ? t("bha.inHole") : depth(run.md_out)}
        </dd>
        <dt>{t("bha.bit.hours")}</dt>
        <dd className="mono">{fmtHours(run.hours, locale)}</dd>
        {run.bit_tfa != null ? (
          <>
            <dt>{t("bha.bit.tfa")}</dt>
            <dd className="mono">{fmtNum(run.bit_tfa, locale, { maximumFractionDigits: 2 })} in²</dd>
          </>
        ) : null}
      </dl>

      {/* dull grade */}
      {dull ? (
        <>
          <h2 className="bha-subhead">{t("bha.dull")}</h2>
          <div className="dull-grid">
            {DULL_KEYS.map((k, i) => (
              <div className="dull-cell" data-own="p" key={`${k}-${i}`}>
                <div className="k">{k}</div>
                <div className="v">{dull[i] ?? "—"}</div>
              </div>
            ))}
          </div>
          {run.pull_reason ? <div className="dull-note">{run.pull_reason}</div> : null}
        </>
      ) : null}

      {/* bit-ops grid */}
      {run.bit_ops.length ? (
        <>
          <h2 className="bha-subhead">{t("bha.bitOps")}</h2>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th className="num">{t("bha.op.md")}</th>
                  <th className="num">{t("bha.op.rop")}</th>
                  <th className="num">{t("bha.op.wob")}</th>
                  <th className="num">{t("bha.op.rpm")}</th>
                  <th className="num">{t("bha.op.flow")}</th>
                </tr>
              </thead>
              <tbody>
                {[...run.bit_ops]
                  .sort((a, b) => a.md - b.md)
                  .map((o) => (
                    <tr key={o.id} data-owner="p">
                      <td className="num tab-nums">{depth(o.md)}</td>
                      <td className="num tab-nums">{fmtNum(o.rop, locale, { maximumFractionDigits: 0 })}</td>
                      <td className="num tab-nums">{fmtNum(o.wob, locale, { maximumFractionDigits: 0 })}</td>
                      <td className="num tab-nums">{fmtNum(o.rpm, locale, { maximumFractionDigits: 0 })}</td>
                      <td className="num tab-nums">{fmtNum(o.flow_rate, locale, { maximumFractionDigits: 0 })}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}

function ComponentRow({
  comp,
  typeLabel,
  vendorLabel,
  len,
}: {
  comp: BhaComponentOut;
  typeLabel: (code: string) => string;
  vendorLabel: (code: string | null) => string;
  len: (v: number | null) => string;
}) {
  const isBit = comp.component_type_entry_key.toUpperCase() === "BIT";
  return (
    <div className={`comp-row${isBit ? " is-bit" : ""}`}>
      <div data-own="m">{typeLabel(comp.component_type_entry_key)}</div>
      <div className="tab-nums" data-own="m">
        {comp.od_in == null ? "—" : inchFrac(comp.od_in)}
      </div>
      <div className="tab-nums" data-own="p">
        {len(comp.length)}
      </div>
      <div data-own="m">{comp.vendor_entry_key ? vendorLabel(comp.vendor_entry_key) : "—"}</div>
    </div>
  );
}
